import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

let CITY_CACHE = null;

export const CityAutocomplete = ({ value, onSelect, placeholder = "City", icon, testId, onChangeText }) => {
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    const loadCities = async () => {
      if (!CITY_CACHE) {
        try {
          const res = await api.get("/cities");
          CITY_CACHE = res.data;
        } catch (e) { CITY_CACHE = []; }
      }
    };
    loadCities();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const runSearch = (q) => {
    const local = (CITY_CACHE || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
    setOptions(local.map((c) => ({ name: c.name, coords: c.coords, source: "local" })));
    if (q.length >= 3) {
      setLoading(true);
      fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=0&limit=5&q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((data) => {
          const remote = (data || [])
            .map((d) => ({ name: d.display_name.split(",")[0], coords: [parseFloat(d.lat), parseFloat(d.lon)], source: "osm", full: d.display_name }))
            .filter((r) => !local.some((l) => l.name.toLowerCase() === r.name.toLowerCase()));
          setOptions((prev) => [...prev, ...remote].slice(0, 8));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  };

  const handleChange = (e) => {
    const q = e.target.value;
    setText(q);
    setOpen(true);
    if (onChangeText) onChangeText(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 250);
  };

  const choose = (opt) => {
    setText(opt.name);
    setOpen(false);
    onSelect && onSelect(opt.name, opt.coords || null);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]">{icon || <MapPin size={16} />}</span>
        <Input
          data-testid={testId}
          value={text}
          onChange={handleChange}
          onFocus={() => { if (text) { runSearch(text); setOpen(true); } }}
          placeholder={placeholder}
          className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-11"
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-white/40" size={14} />}
      </div>
      {open && options.length > 0 && (
        <div className="absolute z-[60] mt-1 w-full glass-solid rounded-lg overflow-hidden shadow-xl max-h-64 overflow-y-auto">
          {options.map((opt, idx) => (
            <button
              type="button"
              key={idx}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
              className="w-full text-left px-3 py-2 hover:bg-white/8 flex items-center gap-2 text-sm"
            >
              <MapPin size={14} className="text-[#22D3EE] shrink-0" />
              <span className="text-white truncate">{opt.name}</span>
              {opt.source === "local" && <span className="ml-auto text-[10px] text-[#F7C948]">popular</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
